<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const props = defineProps<{
    content: CmsElementDetailGallery
}>()

const { images, getImageUrl, getImageAlt, getImageWidth, getImageHeight } = useDetailGallery(props.content)

// Get product from content data using useProduct composable
const { product } = useProduct(
    (props.content?.data?.product ?? null) as Parameters<typeof useProduct>[0]
)

// Transform images to carousel slides format
const carouselSlides = computed(() =>
    images.value.map((image, index) => ({
        id: image.mediaId || `image-${index}`,
        image: getImageUrl(image),
        alt: getImageAlt(image),
        width: getImageWidth(image),
        height: getImageHeight(image)
    }))
)

// Modal state management
const fullscreenModal = useModal('detail-gallery-fullscreen')
const currentSlideIndex = ref(0)
const modalCarouselKey = ref(0)
const fullscreenCarouselRef = ref<{ goToSlide: (index: number) => void } | null>(null)

// Open modal with specific image
const openModal = (index: number) => {
    currentSlideIndex.value = index
    modalCarouselKey.value++
    fullscreenModal.open()
    nextTick(() => {
        setTimeout(() => {
            fullscreenCarouselRef.value?.goToSlide(index)
        }, 150)
    })
}

// Only used to control click/cursor behaviour — NOT for layout switching
// Layout switching is handled purely by CSS (.responsive-grid at lg+)
const bp = useBreakpoints(breakpointsTailwind)
const isDesktop = bp.greaterOrEqual('lg')
</script>

<template>
    <div class="detail-gallery w-full relative">
        <!-- Product Badges on first image -->
        <ProductBadges
            v-if="product"
            :product="product"
            variant="floating"
            class="absolute top-6 left-4 z-10"
        />

        <!-- Wishlist Toggle -->
        <ProductWishlistToggle
            :product="product"
            variant="icon"
            class="absolute top-4 right-4 z-10 shrink-0 lg:hidden"
        />

        <div v-if="images.length > 0">
            <ComponentCarousel
                class="responsive-grid"
                :slides="carouselSlides"
                :label="$t('gallery.productImage')"
                :breakpoints="{
                    default: { slidesPerView: 1, enableArrows: true, enableDots: true },
                    lg:      { slidesPerView: 1, enableArrows: false, enableDots: false }
                }"
                :gap="0"
                slides-class="md:!p-0"
                :enable-arrows="true"
                :enable-dots="true"
                :enable-autoplay="false"
                carousel-width="100%"
                left-arrow-class="btn btn-square bg-transparent z-30 border-none absolute left-0 top-1/2 -translate-y-1/2"
                right-arrow-class="btn btn-square bg-transparent z-30 border-none absolute right-0 top-1/2 -translate-y-1/2"
                dots-container-class="absolute bottom-4 left-0 right-0 flex justify-center gap-2"
            >
                <template #left-arrow="{ previous }">
                    <FoundationButton
                        class="btn-square"
                        :aria-label="$t('carousel.previous')"
                        @click="previous()"
                    >
                        <FoundationIcon name="chevron-left" size="xl" />
                    </FoundationButton>
                </template>

                <template #right-arrow="{ next }">
                    <FoundationButton
                        class="btn-square"
                        :aria-label="$t('carousel.next')"
                        @click="next()"
                    >
                        <FoundationIcon name="chevron-right" size="xl" />
                    </FoundationButton>
                </template>

                <template #default="{ slide, index }">
                    <div
                        class="aspect-[8/9] flex flex-col justify-center items-center bg-base-300 overflow-hidden relative"
                        :class="!isDesktop ? 'px-10 pt-10 cursor-zoom-in' : ''"
                        @click="!isDesktop ? openModal(index) : undefined"
                    >
                        <FoundationImage
                            :src="slide.image"
                            :alt="slide.alt"
                            width="926"
                            height="926"
                            sizes="535px sm:663px md:919px lg:723px xl:872px 2xl:926px"
                            class="w-full object-contain mix-blend-multiply"
                            aspect-ratio="1/1"
                            container-class="flex flex-col items-center w-full"
                            :intersection-lazy="index > 0"
                            :preload="index === 0"
                            :fetchpriority="index === 0 ? 'high' : undefined"
                            :loading="index === 0 ? 'eager' : undefined"
                            root-margin="200px"
                        />
                    </div>
                </template>
            </ComponentCarousel>
        </div>

        <div v-else class="aspect-[8/9] flex flex-col justify-center items-center bg-base-300 overflow-hidden text-base-500">
            <FoundationIcon name="image" size="2xl" />
        </div>

        <!-- Fullscreen Modal -->
        <ComponentModal
            :controller="fullscreenModal"
            :close-on-click-outside="true"
            :fullscreen="true"
            :modal-headline="$t('gallery.fullscreenTitle')"
        >
            <ComponentCarousel
                ref="fullscreenCarouselRef"
                :key="`modal-carousel-${modalCarouselKey}`"
                :slides="carouselSlides"
                :label="$t('gallery.productImage')"
                :breakpoints="{
                    'default': {
                        slidesPerView: 1
                    }
                }"
                :gap="0"
                :enable-arrows="carouselSlides.length > 1"
                left-arrow-class="absolute -left-6 w-12 h-12 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center"
                right-arrow-class="absolute -right-6 w-12 h-12 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center"
                :enable-dots="carouselSlides.length > 1"
                :enable-autoplay="false"
                enable-infinite-scroll
                carousel-width="100%"
            >
                <template #default="{ slide }">
                    <div class="w-full h-full flex items-center justify-center">
                        <FoundationImage
                            :src="slide.image"
                            :alt="slide.alt"
                            class="w-full h-full object-contain"
                            width="916"
                            height="916"
                            sizes="567px sm:711px md:916px"
                            loading="lazy"
                            root-margin="200px"
                        />
                    </div>
                </template>

                <template #left-arrow="{ previous }">
                    <FoundationIcon name="chevron-left" class="w-6 h-6" @click="previous" />
                </template>

                <template #right-arrow="{ next }">
                    <FoundationIcon name="chevron-right" class="w-6 h-6" @click="next" />
                </template>
            </ComponentCarousel>
        </ComponentModal>
    </div>
</template>
